import React, { useState, useEffect } from "react";
import { Box, Typography } from "@mui/material";
import { Grid } from "@material-ui/core";
import CustomModal from "../../components/common/customModal";
import CustomInput from "../../components/common/inputField";
import CustomSelect from "../../components/common/customSelect";
import CustomDatePicker from "../../components/common/customDatePicker";
import dayjs, { Dayjs } from "dayjs";
import dashboardService from "../../services/DashboardServices";
import {
  Holiday,
  HolidayFormDataState,
  HolidayFormErrors,
} from "../../types/dashboard";
import { showToast } from "../../components/common/toastNotification";
import ExcelCSVFileUpload from "../../components/common/fileUpload/excelCSVFileUpload";

interface HolidayModalProps {
  open: boolean;
  onClose: () => void;
  onSave: () => void;
  states: { id: number; name: string }[];
  holidayUpdateData: Holiday;
}

const HolidayModal: React.FC<HolidayModalProps> = ({
  open,
  onClose,
  onSave,
  states,
  holidayUpdateData,
}) => {
  const [formData, setFormData] = useState<HolidayFormDataState>({
    holidayId: null,
    holidayDate: dayjs(),
    holidayName: "",
    stateId: 0,
  });

  const [isFileUploaded, setIsFileUploaded] = useState<boolean>(false);
  const [file, setFile] = useState<File | null>(null);

  const [formErrors, setFormErrors] = useState({
    holidayDate: "",
    holidayName: "",
    stateId: "",
    fileError: "",
  });
  const [btnLoading, setBtnLoading] = useState(false);

  useEffect(() => {
    if (holidayUpdateData) {
      setFormData({
        holidayId: holidayUpdateData.holidayId || null,
        holidayDate: holidayUpdateData.holidayDate
          ? dayjs(holidayUpdateData.holidayDate)
          : dayjs(),
        holidayName: holidayUpdateData.holidayName || "",
        stateId: holidayUpdateData.stateId || 0,
      });
    }
    if (open) {
      setFile(null);
      setIsFileUploaded(false);
      setFormErrors({
        holidayDate: "",
        holidayName: "",
        stateId: "",
        fileError: "",
      });
    }
  }, [holidayUpdateData, open]);

  const handleChange = (
    field: string,
    value: string | number | Dayjs | null
  ) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));

    setFormErrors((prevErrors) => ({
      ...prevErrors,
      [field]: "",
    }));
    setIsFileUploaded(false);
    setFile(null);
  };

  //// form validation code
  const validateForm = () => {
    const errors: HolidayFormErrors = {};

    const isFormEmpty = !formData.holidayName.trim() && !formData.stateId;
    const isFileEmpty = !file;

    if (isFileEmpty && isFormEmpty) {
      errors.fileError = "Either upload a file or fill the form.";
    }

    if (!file) {
      if (!formData.holidayDate) {
        errors.holidayDate = "Holiday date is required.";
      }
      if (!formData.holidayName.trim()) {
        errors.holidayName = "Holiday name is required.";
      }
      if (!formData.stateId) {
        errors.stateId = "Please select a location.";
      }
    }

    setFormErrors({
      holidayDate: errors.holidayDate || "",
      holidayName: errors.holidayName || "",
      stateId: errors.stateId || "",
      fileError: errors.fileError || "",
    });
    return Object.keys(errors).length === 0;
  };

  //// handle APi calling for save and update Holiday data
  const handleSaveData = async () => {
    if (!validateForm()) return;
    setBtnLoading(true);
    if (file) {
      try {
        const formDataToSend = new FormData();
        formDataToSend.append("file", file);

        await dashboardService
          .importHolidaysDataAPI(formDataToSend)
          .then((response) => {
            if (response && response.data && response.data.statusCode === 200) {
              showToast(response.data.message, "success");
              onSave();
              setFile(null);
              setIsFileUploaded(false);
              onClose();
            } else {
              showToast(response?.data?.message, "error");
            }
          })
          .catch((error) => {
            console.error("Error", error);
          })
          .finally(() => {
            setBtnLoading(false);
          });
      } catch (error) {
        console.error("Error saving space data:", error);
      }
    } else {
      const inputData = {
        holidayId: formData.holidayId,
        holidayDate: formData.holidayDate
          ? dayjs(formData.holidayDate).format("YYYY-MM-DD")
          : "",
        holidayName: formData.holidayName,
        stateId: formData.stateId,
      };

      try {
        const response = formData.holidayId
          ? await dashboardService.updateHolidaysDataAPI(inputData)
          : await dashboardService.addHolidaysDataAPI(inputData);

        if (response?.data?.statusCode === 400) {
          showToast(response?.data?.message || "Something went wrong", "error");
        } else {
          showToast("Holiday saved successfully", "success");
          onSave();
          onClose();
        }
        // showToast("Holiday saved successfully", "success");
        onSave();
        onClose();
        setBtnLoading(false);
      } catch (error) {
        console.error("Error saving holiday:", error);
      }
    }
  };

  return (
    <CustomModal
      open={open}
      handleClose={onClose}
      title="Holidays"
      onSave={handleSaveData}
      btnLoading={btnLoading}
    >
      <Box mt={2}>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <ExcelCSVFileUpload
              fileUrl={file instanceof File ? URL.createObjectURL(file) : file}
              onChange={(files) => {
                const uploadedFile = files[0] || null;
                setFile(uploadedFile);
                setIsFileUploaded(files.length > 0 ? true : false);
                setFormErrors({
                  holidayDate: "",
                  holidayName: "",
                  stateId: "",
                  fileError: "",
                });
              }}
            />
          </Grid>
          <Grid item xs={12}>
            <Box sx={{ textAlign: "end" }}>
              {" "}
              <a
                href="https://royalnfieldstrg.blob.core.windows.net/royalenfieldcontainer/SampleDocument/HolidaySampleData.xlsx"
                style={{
                  color: "red",
                  fontWeight: "600",
                  textDecoration: "none",
                }}
              >
                Sample File - FILE
              </a>
            </Box>
          </Grid>

          {formErrors.fileError && (
            <Grid
              item
              xs={12}
              style={{ paddingTop: "0px", paddingBottom: "0px" }}
            >
              <Typography
                color="error"
                sx={{ fontSize: "12px", textAlign: "left" }}
              >
                {formErrors.fileError}
              </Typography>
            </Grid>
          )}

          <Grid item xs={12}>
            <Typography
              variant="subtitle1"
              sx={{
                fontWeight: "medium",
                fontSize: "14px",
                display: "flex",
                justifyContent: "center",
                color: "#CCCCCC",
                paddingTop: "10px",
              }}
            >
              OR
            </Typography>
          </Grid>

          <Grid item xs={12}>
            <CustomDatePicker
              name="holidayDate"
              label="Holiday Date"
              value={formData.holidayDate ? dayjs(formData.holidayDate) : null}
              onChange={(value: Dayjs | null) =>
                handleChange("holidayDate", value)
              }
              error={formErrors.holidayDate}
              readOnly={isFileUploaded}
            />
          </Grid>

          <Grid item xs={12}>
            <CustomInput
              label="Holiday Name"
              type="text"
              name="holidayName"
              placeholder="Enter Holiday Name"
              value={formData.holidayName}
              onChange={(e) => handleChange("holidayName", e.target.value)}
              error={formErrors.holidayName}
              required
              readOnly={isFileUploaded}
            />
          </Grid>

          <Grid item xs={12}>
            <CustomSelect
              name="stateId"
              label="Location"
              value={formData.stateId}
              placeHolder="Select Location"
              onChange={(event) => handleChange("stateId", event.target.value)}
              options={states.map((state) => ({
                label: state.name,
                value: state.id,
              }))}
              error={formErrors.stateId}
              readOnly={isFileUploaded}
              required
            />
          </Grid>
        </Grid>
      </Box>
    </CustomModal>
  );
};

export default HolidayModal;
